import events from './Register';
import { isEmpty, find } from 'lodash';
import { AnalyticsProviders, DefaultProviders, } from './models/Event';
import Strings from '../constants/Strings';
const { GA4, GTM, Braze, EnhancedBraze, MParticle, Branch, FullStory } = AnalyticsProviders;
const { GTMProvider, GA4Provider, BrazeProvider, FSProvider } = DefaultProviders;
class AAnalyticsManager {
    constructor(analyticsObj) {
        this._data = {};
        this.KEY_DEFAULT_ANALYTICS_PROVIDERS = 'default.analytics.providers';
        this.analyticsObj = analyticsObj;
        this._data = {};
    }
    replaceInline(data, replacements) {
        if (data && replacements) {
            const clonedData = { ...data };
            Object.entries(data).forEach(([key, value]) => {
                if (typeof value.includes === typeof Function && value.includes('%')) {
                    clonedData[key] = replacements[value];
                }
            });
            return clonedData;
        }
        return data;
    }
    // Function for replacing the payload for PWA
    replaceInlineForAllNamedParams(dataStr, replacements) {
        if (dataStr && replacements) {
            return dataStr.replace(/%\w+%/g, (all) => {
                if (replacements && replacements[all] && typeof replacements[all] === 'string') {
                    return replacements[all];
                }
                return JSON.stringify(replacements[all]) || all;
            });
        }
        return dataStr;
    }
    getAnalyticsManager(analyticsProvider) {
        return this._data[analyticsProvider];
    }
    set defaultAnalyticsProviders(value) {
        if (isEmpty(value)) {
            throw new Error('defaultAnalyticsProviders must not be empty');
        }
        this._data[this.KEY_DEFAULT_ANALYTICS_PROVIDERS] = value;
    }
    get defaultAnalyticsProviders() {
        return this._data[this.KEY_DEFAULT_ANALYTICS_PROVIDERS];
    }
    // eslint-disable-next-line unused-imports/no-unused-vars
    generatePayload(payload, content) {
        let newPayload;
        let newAttributes;
        if (content) {
            if (payload?.data) {
                newPayload = this.replaceInline(payload.data, content);
            }
            if (payload?.userAttributes) {
                newAttributes = this.replaceInline(payload.userAttributes, content);
            }
        }
        return [newPayload, newAttributes];
    }
    isProviderSupported(event, providerName) {
        return find(event?.payload, (payload) => {
            const provider = find(payload.providers, { name: providerName });
            return !!provider;
        });
    }
    executeDataFn(eventName, content, providerName, defaultProvider, runUserAttributes = false) {
        try {
            const event = events?.[eventName];
            const payload = this.isProviderSupported(event, providerName);
            if (payload) {
                const [newPayload, newAttributes] = this.generatePayload(payload, content);
                const providerInstance = this.analyticsObj?.();
                const provider = find(payload.providers, { name: providerName }) ?? defaultProvider;
                const evtName = payload.eventName ?? eventName;
                provider?.dataFunctions?.forEach((dataFn) => {
                    (async () => {
                        await dataFn(providerInstance, evtName, newPayload ?? {});
                    })();
                });
                if (runUserAttributes && newAttributes && payload.userAttributeFn) {
                    (async () => {
                        await payload?.userAttributeFn?.(providerInstance, evtName, newAttributes);
                    })();
                }
            }
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Error sending ${providerName} event, eventName: ${eventName}, content: ${JSON.stringify(content)}, error: `, e);
        }
    }
}
class GA4AnalyticsManager extends AAnalyticsManager {
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${GA4} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, content, GA4, GA4Provider);
    }
}
class GTMAnalyticsManager extends AAnalyticsManager {
    generatePayload(payload, content) {
        let newPayload;
        const userAttributes = undefined;
        if (content && payload?.data) {
            newPayload = this.replaceInlineForAllNamedParams(JSON.stringify(payload?.data), content);
            newPayload = JSON.parse(newPayload);
        }
        else {
            newPayload = content.data;
        }
        return [newPayload, userAttributes];
    }
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${GTM} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, content, GTM, GTMProvider);
    }
}
class BrazeAnalyticsManager extends AAnalyticsManager {
    updateCurrencyToUSD(content) {
        const clonedContent = JSON.parse(JSON.stringify(content) ?? '{}');
        if ('%currency%' in clonedContent) {
            clonedContent['%currency%'] = Strings.usd;
        }
        return clonedContent;
    }
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${Braze} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, this.updateCurrencyToUSD(content), Braze, BrazeProvider, true);
    }
}
class MParticleAnalyticsManager extends AAnalyticsManager {
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${MParticle} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, content, MParticle, undefined, true);
    }
}
class FSAnalyticsManager extends AAnalyticsManager {
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${FullStory} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, content, FullStory, FSProvider);
    }
}
class BranchAnalyticsManager extends AAnalyticsManager {
    registerEvent(eventName, content) {
        // eslint-disable-next-line no-console
        console.log(`sending ${Branch} event for eventName: ${eventName} and content: ${JSON.stringify(content)}`);
        this.executeDataFn(eventName, content, Branch, undefined, true);
    }
}
class AnalyticsManager extends AAnalyticsManager {
    setAnalyticsManagerForProvider(analyticsProvider) {
        for (const [provider, analyticsObj] of Object.entries(analyticsProvider ?? {})) {
            // eslint-disable-next-line no-console
            console.log('Setting AnalyticsManager instance for provider: ', provider);
            let analyticsManager;
            switch (provider) {
                case GA4:
                    analyticsManager = new GA4AnalyticsManager(analyticsObj);
                    break;
                case GTM:
                    analyticsManager = new GTMAnalyticsManager(analyticsObj);
                    break;
                case Braze:
                    analyticsManager = new BrazeAnalyticsManager(analyticsObj);
                    break;
                case EnhancedBraze:
                    analyticsManager = new BrazeAnalyticsManager(analyticsObj);
                    break;
                case MParticle:
                    analyticsManager = new MParticleAnalyticsManager(analyticsObj);
                    break;
                case Branch:
                    analyticsManager = new BranchAnalyticsManager(analyticsObj);
                    break;
                case FullStory:
                    analyticsManager = new FSAnalyticsManager(analyticsObj);
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.error('Unknown provider: ', provider);
            }
            // eslint-disable-next-line no-console
            console.log(`AnalyticsManager instance for provider ${provider}: ${analyticsManager}`);
            this._data[provider] = analyticsManager;
        }
    }
    registerAnalyticsProviders(analyticsProviders) {
        if (isEmpty(analyticsProviders)) {
            throw new Error('analyticsProviders must not be empty');
        }
        analyticsProviders?.forEach((analyticsProvider) => this.setAnalyticsManagerForProvider(analyticsProvider));
    }
    deregisterAnalyticsProviders() {
        this._data[this.KEY_DEFAULT_ANALYTICS_PROVIDERS] = [];
    }
    async registerEvent(eventName, content, analyticsObj, analyticsProviders) {
        // eslint-disable-next-line no-console
        console.log('custom analyticsObject', analyticsObj);
        const providers = analyticsProviders ?? this.defaultAnalyticsProviders ?? [];
        for (const provider of providers) {
            const analyticsManager = this.getAnalyticsManager(provider);
            await analyticsManager?.registerEvent?.(eventName, content);
        }
        return eventName;
    }
}
const AnalyticsService = new AnalyticsManager();
Object.freeze(AnalyticsService);
export { AnalyticsService };
